import { Box, Divider, List, ListItem, Typography } from '@mui/material';
import type { TFunction } from 'i18next';
import { mergeSx } from '../../../../utils/styles';
import { formatDisplayDate, formatScore, matchesTeamIdentifier } from '../helpers';
import { styles, WIDGET_COLORS } from '../styles';
import type { PreMatchWidgetData, PreMatchWidgetTabId } from '../types';
import LineupsPanel from './LineupsPanel';
import { ResponsiveTeamMatchesPanels, TeamFormPanel, UpcomingMatchesPanel } from './MatchPanels';
import MatchSummaryHero from './MatchSummaryHero';
import StandingsPanel from './StandingsPanel';
import TeamVisual from './TeamVisual';

type TabContentProps = {
  tab: PreMatchWidgetTabId;
  data: PreMatchWidgetData;
  t: TFunction;
};

export const renderDefaultTabContent = ({ tab, data, t }: TabContentProps) => {
  const h2hSummary = data.headToHeadSummary;
  const h2hTotal = h2hSummary ? h2hSummary.homeWins + h2hSummary.draws + h2hSummary.awayWins : 0;
  const homeShortName = data.fixture.home.shortName ?? data.fixture.home.name;
  const awayShortName = data.fixture.away.shortName ?? data.fixture.away.name;
  const getHeadToHeadTeamDisplay = (teamName: string, participantId?: string | null, teamShortName?: string | null) => {
    if (matchesTeamIdentifier(data.fixture.home, teamName, teamShortName)) {
      return {
        team: {
          ...data.fixture.home,
          participantId: participantId ?? data.fixture.home.participantId,
        },
        shortName: homeShortName,
        fullName: data.fixture.home.name,
      };
    }

    if (matchesTeamIdentifier(data.fixture.away, teamName, teamShortName)) {
      return {
        team: {
          ...data.fixture.away,
          participantId: participantId ?? data.fixture.away.participantId,
        },
        shortName: awayShortName,
        fullName: data.fixture.away.name,
      };
    }

    return {
      team: {
        name: teamName,
        shortName: teamName,
        participantId: participantId ?? null,
        jersey: null,
      },
      shortName: teamName,
      fullName: teamName,
    };
  };

  switch (tab) {
    case 'preview-summary':
      return (
        <Box sx={styles.summaryViewport}>
          <MatchSummaryHero data={data} />
        </Box>
      );
    case 'standings':
      return <StandingsPanel data={data} />;
    case 'lineups':
      return <LineupsPanel data={data} />;
    case 'head-to-head':
      if (!h2hSummary) {
        return (
          <Box>
            <Typography sx={styles.summaryDescription}>{t('preMatchWidget.headToHead.empty')}</Typography>
          </Box>
        );
      }
      return (
        <Box>
          <Box sx={styles.h2hHero}>
            <Box sx={styles.h2hHeroTeam}>
              <TeamVisual
                competitionName={data.competition.name}
                team={data.fixture.home}
                sx={styles.h2hHeroTeamVisual}
              />
              <Typography sx={styles.h2hHeroTeamLabel}>{homeShortName}</Typography>
            </Box>
            <Typography sx={styles.h2hHeroVs}>VS</Typography>
            <Box sx={mergeSx(styles.h2hHeroTeam, styles.h2hHeroTeamRight)}>
              <TeamVisual
                competitionName={data.competition.name}
                team={data.fixture.away}
                sx={styles.h2hHeroTeamVisual}
              />
              <Typography sx={styles.h2hHeroTeamLabel}>{awayShortName}</Typography>
            </Box>
          </Box>
          <Typography sx={styles.panelHeader}>
            {t('preMatchWidget.headToHead.directMatches', {
              count: h2hSummary.totalMatches,
            })}
          </Typography>
          <Box sx={styles.summaryBar}>
            <Box sx={{ flex: h2hSummary.homeWins || 0.001, backgroundColor: WIDGET_COLORS.danger }} />
            <Box sx={{ flex: h2hSummary.draws || 0.001, backgroundColor: WIDGET_COLORS.text }} />
            <Box sx={{ flex: h2hSummary.awayWins || 0.001, backgroundColor: WIDGET_COLORS.success }} />
          </Box>
          <Box sx={styles.headToHeadStats}>
            <Box sx={styles.h2hStatBlock}>
              <Typography sx={{ ...styles.h2hStatValue, color: WIDGET_COLORS.danger }}>
                {h2hSummary.homeWins}
              </Typography>
              <Typography sx={{ ...styles.h2hStatLabel, color: WIDGET_COLORS.danger }}>
                {t('preMatchWidget.headToHead.winLabel')}
              </Typography>
            </Box>
            <Box sx={styles.h2hStatBlock}>
              <Typography sx={styles.h2hStatValue}>{h2hSummary.draws}</Typography>
              <Typography sx={styles.h2hStatLabel}>{t('preMatchWidget.headToHead.drawLabel')}</Typography>
            </Box>
            <Box sx={styles.h2hStatBlock}>
              <Typography sx={{ ...styles.h2hStatValue, color: WIDGET_COLORS.success }}>
                {h2hSummary.awayWins}
              </Typography>
              <Typography sx={{ ...styles.h2hStatLabel, color: WIDGET_COLORS.success }}>
                {t('preMatchWidget.headToHead.winLabel')}
              </Typography>
            </Box>
          </Box>
          {h2hTotal > 0 ? <Divider sx={{ borderColor: WIDGET_COLORS.overlayBorder, mb: 1.5 }} /> : null}
          <Typography sx={styles.panelHeader}>{t('preMatchWidget.headToHead.last5DirectMatches')}</Typography>
          {data.headToHead?.length ? (
            <List sx={styles.directMatchList}>
              {data.headToHead.map((match) => {
                const homeTeamDisplay = getHeadToHeadTeamDisplay(
                  match.homeTeam,
                  match.homeParticipantId,
                  match.homeTeamShortName
                );
                const awayTeamDisplay = getHeadToHeadTeamDisplay(
                  match.awayTeam,
                  match.awayParticipantId,
                  match.awayTeamShortName
                );

                return (
                  <ListItem key={`${match.date}-${match.homeTeam}-${match.awayTeam}`} sx={styles.directMatchItem}>
                    <Typography sx={styles.matchMeta}>
                      {formatDisplayDate(match.date, false)} {match.competition}
                    </Typography>
                    <Box sx={styles.directMatchRow}>
                      <Box sx={styles.directMatchTeamGroup}>
                        <TeamVisual
                          competitionName={data.competition.name}
                          team={homeTeamDisplay.team}
                          sx={styles.directMatchTeamVisual}
                        />
                        <Typography sx={mergeSx(styles.directMatchTeam, styles.directMatchTeamMobile)}>
                          {match.homeTeamShortName ?? match.homeTeam}
                        </Typography>
                        <Typography sx={mergeSx(styles.directMatchTeam, styles.directMatchTeamDesktop)}>
                          {homeTeamDisplay.fullName}
                        </Typography>
                      </Box>
                      <Box sx={styles.scorePill}>
                        <Typography sx={{ typography: 'inherit', fontWeight: 'inherit', color: 'inherit' }}>
                          {formatScore(match.score) ?? '-'}
                        </Typography>
                      </Box>
                      <Box sx={mergeSx(styles.directMatchTeamGroup, styles.directMatchTeamGroupRight)}>
                        <TeamVisual
                          competitionName={data.competition.name}
                          team={awayTeamDisplay.team}
                          sx={styles.directMatchTeamVisual}
                        />
                        <Typography sx={mergeSx(styles.directMatchTeam, styles.directMatchTeamMobile)}>
                          {match.awayTeamShortName ?? match.awayTeam}
                        </Typography>
                        <Typography sx={mergeSx(styles.directMatchTeam, styles.directMatchTeamDesktop)}>
                          {awayTeamDisplay.fullName}
                        </Typography>
                      </Box>
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          ) : (
            <Typography sx={styles.summaryDescription}>{t('preMatchWidget.headToHead.empty')}</Typography>
          )}
        </Box>
      );
    case 'last-5-matches':
      return (
        <Box>
          <Typography sx={styles.panelHeader}>{t('preMatchWidget.matches.last5Title')}</Typography>
          <ResponsiveTeamMatchesPanels
            homeTeam={data.fixture.home}
            awayTeam={data.fixture.away}
            homeMatches={data.last5Matches.home}
            awayMatches={data.last5Matches.away}
            tablistLabel={t('preMatchWidget.aria.last5Tablist')}
            mobileHeroLabel={data.tournament.name}
            competitionName={data.competition.name}
            panelSx={styles.selectedTabSurfacePanel}
            mobilePanelSx={styles.selectedTabSurfacePanel}
            renderPanel={(props) => <TeamFormPanel {...props} hideTeamHeader />}
          />
        </Box>
      );
    case 'next-5-matches':
      return (
        <Box>
          <Typography sx={styles.panelHeader}>{t('preMatchWidget.matches.next5Title')}</Typography>
          <ResponsiveTeamMatchesPanels
            homeTeam={data.fixture.home}
            awayTeam={data.fixture.away}
            homeMatches={data.next5Matches.home}
            awayMatches={data.next5Matches.away}
            tablistLabel={t('preMatchWidget.aria.next5Tablist')}
            mobileHeroLabel={data.tournament.name}
            competitionName={data.competition.name}
            panelSx={styles.selectedTabSurfacePanel}
            mobilePanelSx={styles.selectedTabSurfacePanel}
            renderPanel={(props) => <UpcomingMatchesPanel {...props} />}
          />
        </Box>
      );
    default:
      return null;
  }
};
